import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Layout, Menu, Button, Dropdown, Avatar, Space, Typography, Badge, Drawer } from 'antd';
import AdminSidebar from './AdminSidebar';
import {
  ShoppingOutlined,
  ShoppingCartOutlined,
  UserOutlined,
  LogoutOutlined,
  LoginOutlined,
  DashboardOutlined,
  CalendarOutlined,
  HeartOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  MailOutlined,
  FileTextOutlined,
  CrownOutlined,
  LockOutlined,
  SettingOutlined,
  ShopOutlined,
  MenuOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { useStores } from '../../stores';
import AnimapLogo from '../../assets/Animap_Logo_Color.png';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationBell from '../notifications/NotificationBell';
import ImpersonationBanner from '../ImpersonationBanner';
import './MainLayout.scss';

const { Header, Content, Footer } = Layout;
const { Text } = Typography;

const MainLayout = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authStore, cartStore, favoriteStore, notificationStore } = useStores();
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Check if on admin pages
  const isOnAdminPages = location.pathname.startsWith('/admin/');

  // Fetch cart, favorites, and start notification polling when user is authenticated
  useEffect(() => {
    if (authStore.isAuthenticated) {
      cartStore.fetchCart();
      favoriteStore.fetchAllFavorites();
      notificationStore.fetchUnreadCount();
    }
  }, [authStore.isAuthenticated, cartStore, favoriteStore, notificationStore]);

  // Track location changes to ensure proper re-rendering
  useEffect(() => {
    // This effect helps components re-render on navigation
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleLogout = () => {
    authStore.logout();
    navigate('/login');
  };

  // Get dashboard path based on user role
  const getDashboardPath = () => {
    if (authStore.isAdmin) return '/admin/dashboard';
    if (authStore.isPartner) return '/partner/dashboard';
    return '/';
  };

  // Check if currently on dashboard
  const isOnDashboard = () => {
    return location.pathname.includes('/admin/') || location.pathname.includes('/partner/');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: t('layout.header.profile'),
      onClick: () => navigate('/profile'),
    },
    {
      key: 'my-pets',
      icon: <HeartOutlined />,
      label: t('layout.header.myAnimals'),
      onClick: () => navigate('/my-pets'),
    },
    // Pet Calendar - hidden until fully ready
    // {
    //   key: 'pet-calendar',
    //   icon: <CalendarOutlined style={{ color: '#52c41a' }} />,
    //   label: t('premium.features.calendar.title'),
    //   onClick: () => navigate('/pets/calendar'),
    // },
    {
      key: 'my-favorites',
      icon: <HeartOutlined style={{ color: '#ff4d4f' }} />,
      label: t('layout.header.myFavorites'),
      onClick: () => navigate('/my-favorites'),
    },
    {
      key: 'my-orders',
      icon: <ShoppingOutlined />,
      label: t('layout.header.myOrders'),
      onClick: () => navigate('/orders'),
    },
    {
      key: 'my-appointments',
      icon: <CalendarOutlined />,
      label: t('layout.header.myAppointments'),
      onClick: () => navigate('/my-appointments'),
    },
    {
      key: 'my-inquiries',
      icon: <QuestionCircleOutlined />,
      label: t('inquiries.generalTitle'),
      onClick: () => navigate('/my-inquiries'),
    },
    {
      type: 'divider',
    },
    {
      key: 'mySubscription',
      icon: <CrownOutlined style={{ color: '#faad14' }} />,
      label: t('layout.header.mySubscription'),
      onClick: () => navigate('/premium'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('layout.header.logout'),
      onClick: handleLogout,
    },
  ];

  // Partner menu - excludes Premium Club (only for regular users)
  const partnerMenuItems = [
    {
      key: 'dashboard',
      icon: <DashboardOutlined />,
      label: t('layout.header.partnerDashboard'),
      onClick: () => navigate('/partner/dashboard'),
    },
    ...userMenuItems.filter(item => item.key !== 'mySubscription'),
  ];

  // Admin menu - simplified without personal items (My pets, My orders, etc.)
  const adminMenuItems = [
    {
      key: 'admin',
      icon: <DashboardOutlined />,
      label: t('layout.header.adminDashboard'),
      onClick: () => navigate('/admin/dashboard'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('layout.header.logout'),
      onClick: handleLogout,
    },
  ];

  const getMenuItems = () => {
    if (authStore.isAdmin) return adminMenuItems;
    if (authStore.isPartner) return partnerMenuItems;
    return userMenuItems;
  };

  // Get current selected menu key based on path
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path.startsWith('/products')) return 'products';
    if (path.startsWith('/services')) return 'services';
    if (path.startsWith('/partners')) return 'partners';
    if (path.startsWith('/faq')) return 'faq';
    return '';
  };

  // Menu items - Products/Services/Partners only visible when authenticated
  const mainMenuItems = authStore.isAuthenticated
    ? [
        {
          key: 'home',
          label: <Link to="/">{t('layout.header.home')}</Link>,
        },
        {
          key: 'products',
          label: <Link to="/products">{t('layout.header.products')}</Link>,
        },
        {
          key: 'services',
          label: <Link to="/services">{t('layout.header.services')}</Link>,
        },
        {
          key: 'partners',
          label: <Link to="/partners">{t('layout.header.partners')}</Link>,
        },
        {
          key: 'faq',
          label: <Link to="/faq">{t('layout.footer.faq')}</Link>,
        },
      ]
    : [
        {
          key: 'home',
          label: <Link to="/">{t('layout.header.home')}</Link>,
        },
        {
          key: 'faq',
          label: <Link to="/faq">{t('layout.footer.faq')}</Link>,
        },
      ];

  // Check if user is admin for different styling
  const isAdminUser = authStore.isAdmin;

  return (
    <Layout className={`main-layout ${isAdminUser ? 'admin-layout' : ''} ${authStore.isImpersonating ? 'impersonating' : ''}`}>
      {/* Impersonation Banner */}
      <ImpersonationBanner />

      <Header className={`site-header ${isAdminUser ? 'admin-header' : ''}`} style={authStore.isImpersonating ? { marginTop: 40 } : {}}>
        <div className="header-content">
          {/* Mobile menu button */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            onClick={() => setMobileMenuOpen(true)}
            className="mobile-menu-button"
          />

          <div className="logo" onClick={() => navigate('/')}>
            <img src={AnimapLogo} alt="Animap" className="logo-image" />
          </div>

          <Menu
            mode="horizontal"
            items={mainMenuItems}
            selectedKeys={[getSelectedKey()]}
            className="main-menu"
          />

          <div className="header-actions">
            <LanguageSwitcher />

            {/* Notification bell - visible for all authenticated users */}
            {authStore.isAuthenticated && (
              <NotificationBell />
            )}

            {/* Hide cart for admin users - admins don't make purchases */}
            {authStore.isAuthenticated && !authStore.isAdmin && (
              <Badge count={cartStore.totalItems} showZero={false} className="cart-badge">
                <Button
                  type="text"
                  icon={<ShoppingCartOutlined style={{ fontSize: '20px' }} />}
                  onClick={() => navigate('/cart')}
                  className="cart-button"
                />
              </Badge>
            )}

            {authStore.isAuthenticated ? (
              <>
                {/* Dashboard button for partners/admins - left of user menu */}
                {(authStore.isPartner || authStore.isAdmin) && (
                  <Button
                    type={isOnDashboard() ? "primary" : "default"}
                    icon={<DashboardOutlined />}
                    onClick={() => navigate(getDashboardPath())}
                    className="dashboard-button"
                  >
                    <span className="dashboard-button-text">Dashboard</span>
                  </Button>
                )}
                <Dropdown menu={{ items: getMenuItems() }} placement="bottomRight">
                  <Space className="user-menu">
                    <Avatar
                      style={{
                        backgroundColor: '#9FB18C',
                        cursor: 'pointer',
                      }}
                      icon={<UserOutlined />}
                    />
                    <Text className="user-name">
                      {authStore.user?.first_name}
                    </Text>
                  </Space>
                </Dropdown>
              </>
            ) : (
              <Space className="auth-buttons">
                <Button
                  type="text"
                  icon={<LoginOutlined />}
                  onClick={() => navigate('/login')}
                  className="login-button"
                >
                  <span className="button-text">{t('layout.header.login')}</span>
                </Button>
                <Button
                  type="primary"
                  onClick={() => navigate('/register')}
                  className="register-button"
                >
                  <span className="button-text">{t('layout.header.register')}</span>
                </Button>
              </Space>
            )}
          </div>
        </div>
      </Header>

      {/* Mobile Navigation Drawer */}
      <Drawer
        title={
          <div className="mobile-drawer-header">
            <img src={AnimapLogo} alt="Animap" style={{ height: 32 }} />
          </div>
        }
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        className="mobile-nav-drawer"
        width={280}
      >
        <Menu
          mode="vertical"
          items={mainMenuItems}
          selectedKeys={[getSelectedKey()]}
          onClick={() => setMobileMenuOpen(false)}
          className="mobile-menu"
        />

        {!authStore.isAuthenticated && (
          <div className="mobile-auth-buttons">
            <Button
              type="primary"
              block
              icon={<LoginOutlined />}
              onClick={() => {
                navigate('/login');
                setMobileMenuOpen(false);
              }}
              style={{ marginBottom: 12 }}
            >
              {t('layout.header.login')}
            </Button>
            <Button
              block
              onClick={() => {
                navigate('/register');
                setMobileMenuOpen(false);
              }}
            >
              {t('layout.header.register')}
            </Button>
          </div>
        )}

        {authStore.isAuthenticated && (
          <div className="mobile-user-section">
            <div className="mobile-user-info">
              <Avatar
                style={{ backgroundColor: '#9FB18C' }}
                icon={<UserOutlined />}
              />
              <Text strong>{authStore.user?.first_name}</Text>
            </div>
            <Menu
              mode="vertical"
              items={getMenuItems()}
              onClick={() => setMobileMenuOpen(false)}
              className="mobile-user-menu"
            />
          </div>
        )}
      </Drawer>

      {isOnAdminPages && authStore.isAdmin ? (
        <Layout style={{ minHeight: 'calc(100vh - 64px - 70px)' }}>
          <AdminSidebar
            collapsed={sidebarCollapsed}
            onCollapse={setSidebarCollapsed}
          />
          <Content className="site-content admin-content" style={{ marginLeft: 0 }}>
            <Outlet key={location.pathname} />
          </Content>
        </Layout>
      ) : (
        <Content className="site-content">
          <Outlet key={location.pathname} />
        </Content>
      )}

      <Footer className="site-footer">
        <div className="footer-content">
          <div className="footer-section">
            <div className="footer-logo">
              <img src={AnimapLogo} alt="Animap" className="footer-logo-image" />
            </div>
            <Text className="footer-text">
              Your trusted partner in animal care!
            </Text>
          </div>
          <div className="footer-links">
            <Space size="large" wrap style={{ marginBottom: 8 }}>
              <Link to="/faq" style={{ color: '#666' }}>
                <QuestionCircleOutlined /> {t('layout.footer.faq')}
              </Link>
              <Link to="/terms" style={{ color: '#666' }}>
                <FileTextOutlined /> {t('layout.footer.terms')}
              </Link>
              <Link to="/privacy" style={{ color: '#666' }}>
                <LockOutlined /> {t('layout.footer.privacy')}
              </Link>
              <Link to="/cookies" style={{ color: '#666' }}>
                <SettingOutlined /> {t('layout.footer.cookies')}
              </Link>
              <Link to="/business-terms" style={{ color: '#666' }}>
                <ShopOutlined /> {t('layout.footer.businessTerms')}
              </Link>
              <a href="mailto:support@animap.gr" style={{ color: '#666' }}>
                <MailOutlined /> {t('layout.footer.contact')}
              </a>
            </Space>
            <div>
              <Text className="footer-text">
                {t('layout.footer.copyright', { year: new Date().getFullYear() })}
              </Text>
            </div>
          </div>
        </div>
      </Footer>
    </Layout>
  );
});

export default MainLayout;
